// Hideo - scripts state that outlives the pages:
// - the Scripts page (src/pages/ScriptsPage.vue): the search text, the "No video" filter and which year and month
//   groups of the "Published" list are open (kept while moving between pages, like before), and the "Attach" dialog (ScriptLinkDialog.vue): the script being attached and the dialog's
//   search. Esc closes the dialog through closeLink (src/features/keyboard.ts);
// - the script editor's data actions: which script is open (editingId, read by the autosave), opening, saving the open
//   tab, tabs, templates, delete. The editor store (src/stores/editor.ts) reads the editor and passes its text here; the
//   data logic is src/features/scripts.ts. Saves without redrawing (saveAppData(d, true)), like the legacy editor.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useDataStore } from './data'
import { uiConfirm, uiPrompt } from './notices'
import { t } from '../i18n'
import type { ProfileData, Script } from '../data/types'
import {
  addTab, deleteScript, deleteTab, ensureTabs, newTabName, renameTab, scriptOf, templateFrom, writeScript, type EditorText
} from '../features/scripts'
import { saveAppData } from '../features/storage/save'

export const useScriptsStore = defineStore('scripts', () => {
  const query = ref('')
  /** "No video" filter */
  const orphans = ref(false)
  /** Year and month groups of the "Published" list the user opened (true) or closed (false); the others follow the
   *  default (the most recent year and month open). Kept while moving between pages, not saved */
  const pubOpen = ref<Record<string, boolean>>({})
  /** The same while searching (every group with a match open), emptied when the search changes */
  const searchOpen = ref<Record<string, boolean>>({})
  /** Script of the open "Attach" dialog ('' = closed) */
  const linkId = ref('')
  const linkQuery = ref('')
  function openLink(id: string): void { linkId.value = id; linkQuery.value = '' }
  function closeLink(): void { linkId.value = '' }

  // ===== Script editor =====
  const data = useDataStore()
  /** Script open in the editor (null = closed) */
  const editingId = ref<string | null>(null)
  /** [data, open script] (the comments and toolbar stores) */
  const current = (): [ProfileData, Script | undefined] => scriptOf(data.getData(), editingId.value)
  const save = (d: ProfileData) => saveAppData(d, true)

  /** Opens a script in the editor: it gets its tabs (in memory, saved with the first save); null = not found */
  function open(id: string): Script | null {
    const sc = data.getData().scripts.find(x => x.id === id)
    if (!sc) return null
    ensureTabs(sc)
    editingId.value = id
    return sc
  }
  /** The editor closed (it saved first) */
  function close(): void { editingId.value = null }
  /** Saves the editor's text into the open tab. read() is called only when a script is open (it reads the editor).
   *  false = nothing open, or the script is gone */
  function saveText(read: () => EditorText): boolean {
    if (!editingId.value) return false
    const [d, sc] = current()
    if (!sc) return false
    writeScript(sc, read())
    save(d)
    return true
  }
  /** Deletes the open script after asking; its videos lose it. true = deleted (the editor closes) */
  async function remove(): Promise<boolean> {
    if (!editingId.value || !(await uiConfirm(t('Delete this script permanently?'), { ok: t('Delete'), danger: true }))) return false
    const d = data.getData(), id = editingId.value
    if (!id) return false
    deleteScript(d, id)
    save(d)
    editingId.value = null
    return true
  }
  /** "Save as template": asks the name (the script's title proposed). true = saved. flush() saved the editor first */
  async function saveTemplate(title: string): Promise<boolean> {
    const name = await uiPrompt(t('Template name:'), title, { ok: t('Save'), icon: 'fa-bookmark' })
    if (!name || !name.trim()) return false
    const [d, sc] = current()
    if (!sc) return false
    const tpl = templateFrom(sc, name)
    if (!tpl) return false
    d.templates.push(tpl)
    save(d)
    return true
  }
  /** Opens another tab (the editor was saved first); the script to show, or null */
  function goTab(id: string): Script | null {
    const [d, sc] = current()
    if (!sc) return null
    sc.activeTab = id
    save(d)
    return sc
  }
  /** New tab: asks its name ("Draft <n>" proposed); flush() saves the editor's text, before the question and again
   *  after it, like before. The script to show (the new tab open), or null */
  async function newTab(flush: () => void): Promise<Script | null> {
    flush()
    const sc0 = current()[1]
    if (!sc0) return null
    const name = await uiPrompt(t('Tab name:'), newTabName(sc0), { ok: t('Create') })
    if (!name || !name.trim()) return null
    flush()
    const [d, sc] = current()
    if (!sc || !addTab(sc, name)) return null
    save(d)
    return sc
  }
  /** Renames a tab (null = cancelled); the editor was saved first. The script, to draw the tabs again */
  function tabRename(id: string, name: string | null): Script | undefined {
    const [d, sc] = current()
    if (sc && renameTab(sc, id, name)) save(d)
    return sc
  }
  /** Deletes a tab after asking (never the last one); flush() saves the editor's text after the answer. The script to
   *  show, or null */
  async function tabDelete(id: string, flush: () => void): Promise<Script | null> {
    if (!(await uiConfirm(t('Delete this tab?'), { ok: t('Delete'), danger: true }))) return null
    flush()
    const [d, sc] = current()
    if (!sc || !deleteTab(sc, id)) return null
    save(d)
    return sc
  }

  return {
    query, orphans, pubOpen, searchOpen, linkId, linkQuery, openLink, closeLink,
    editingId, current, open, close, saveText, remove, saveTemplate, goTab, newTab, tabRename, tabDelete
  }
})
